import React from "react";
import { motion } from "framer-motion";

/**
 * Vansha Graphics — richer, more visible, more colorful abstract SVGs.
 * Marigold & Aubergine palette.
 *
 * Palette references:
 *   Vermilion  #E8552B
 *   Marigold   #F4A72C
 *   Sage       #7BA098
 *   Aubergine  #1B1533
 *   Blush      #FBE4D6
 *   Cream      #FBF4E8
 */

const V = "#E8552B";
const M = "#F4A72C";
const S = "#7BA098";
const A = "#1B1533";
const B = "#FBE4D6";

/* ============ AMBIENT (background decoration) ============ */

// Sun with radiating petals — warm hero decoration
export function SunBloom({ size = 320, className = "", opacity = 1 }) {
  const petals = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className={className} style={{ opacity }} aria-hidden data-testid="gfx-sun-bloom">
      <svg viewBox="0 0 400 400" width={size} height={size} className="max-w-full">
        {petals.map((i) => {
          const a = (i / petals.length) * 360;
          return (
            <motion.ellipse
              key={i}
              cx="200"
              cy="80"
              rx="16"
              ry="52"
              fill={i % 2 ? M : V}
              opacity={i % 2 ? 0.7 : 0.85}
              transform={`rotate(${a} 200 200)`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: i % 2 ? 0.7 : 0.85 }}
              transition={{ duration: 0.9, delay: i * 0.05, ease: "easeOut" }}
              style={{ transformOrigin: "200px 200px" }}
            />
          );
        })}
        <motion.circle
          cx="200" cy="200" r="46" fill={A}
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ duration: 0.7, delay: 0.8 }}
        />
        <motion.circle
          cx="200" cy="200" r="22" fill={M}
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ duration: 0.7, delay: 1.1 }}
        />
      </svg>
    </div>
  );
}

// Layered mountain / horizon — journey metaphor
export function HorizonLayers({ width = 480, height = 240, className = "", opacity = 1 }) {
  return (
    <div className={className} style={{ opacity }} aria-hidden data-testid="gfx-horizon">
      <svg viewBox="0 0 480 240" width={width} height={height} className="max-w-full">
        <motion.circle cx="360" cy="80" r="42" fill={M}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
        <motion.path
          d="M 0 200 Q 80 140 160 170 T 320 160 T 480 170 L 480 240 L 0 240 Z"
          fill={S} opacity="0.9"
          initial={{ y: 40, opacity: 0 }} whileInView={{ y: 0, opacity: 0.9 }} viewport={{ once: true }} transition={{ duration: 0.9 }} />
        <motion.path
          d="M 0 220 Q 120 180 240 200 T 480 210 L 480 240 L 0 240 Z"
          fill={V} opacity="0.85"
          initial={{ y: 40, opacity: 0 }} whileInView={{ y: 0, opacity: 0.85 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1 }} />
        <motion.path
          d="M 0 230 Q 160 215 320 225 T 480 230 L 480 240 L 0 240 Z"
          fill={A}
          initial={{ y: 40 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 }} />
      </svg>
    </div>
  );
}

// Blooming lotus (side view) — motherhood
export function LotusBloom({ size = 260, className = "", opacity = 1 }) {
  return (
    <div className={className} style={{ opacity }} aria-hidden data-testid="gfx-lotus">
      <svg viewBox="0 0 300 300" width={size} height={size} className="max-w-full">
        {[-60, -30, 0, 30, 60].map((r, i) => (
          <motion.ellipse
            key={r}
            cx="150" cy="180" rx="30" ry="80"
            fill={i % 2 ? V : M}
            opacity={0.85}
            transform={`rotate(${r} 150 220)`}
            style={{ transformOrigin: "150px 220px" }}
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 0.85 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: i * 0.1 }}
          />
        ))}
        <motion.ellipse cx="150" cy="150" rx="24" ry="70" fill={A}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.7 }}
        />
        <motion.rect x="146" y="220" width="8" height="60" fill={S}
          initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }}
          transition={{ delay: 1 }} style={{ transformOrigin: "top" }}
        />
      </svg>
    </div>
  );
}

/* ============ ICON-STYLE (small graphics for cards) ============ */

// Colorful ring stack — good for stat cards
export function RingStack({ size = 90, className = "", primary = V, secondary = M }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden data-testid="gfx-ring-stack">
      <circle cx="50" cy="50" r="42" fill="none" stroke={A} strokeWidth="1" opacity="0.2" />
      <motion.circle cx="50" cy="50" r="42" fill="none" stroke={primary} strokeWidth="3"
        strokeDasharray="180 300" strokeLinecap="round"
        initial={{ pathLength: 0, rotate: -90 }} animate={{ pathLength: 1, rotate: 270 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        style={{ transformOrigin: "50% 50%" }}
      />
      <circle cx="50" cy="50" r="28" fill={secondary} opacity="0.18" />
      <circle cx="50" cy="50" r="14" fill={primary} />
    </svg>
  );
}

// Little seed sprouting — perfect for milestones
export function SproutIcon({ size = 88, className = "" }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden data-testid="gfx-sprout">
      <circle cx="50" cy="70" r="22" fill={A} />
      <path d="M 50 70 Q 40 50 30 42" fill="none" stroke={S} strokeWidth="4" strokeLinecap="round" />
      <path d="M 50 70 Q 60 50 70 42" fill="none" stroke={S} strokeWidth="4" strokeLinecap="round" />
      <ellipse cx="30" cy="42" rx="12" ry="7" fill={S} transform="rotate(-30 30 42)" />
      <ellipse cx="70" cy="42" rx="12" ry="7" fill={V} transform="rotate(30 70 42)" />
      <circle cx="50" cy="24" r="6" fill={M} />
    </svg>
  );
}

// Little envelope — for letters
export function EnvelopeIcon({ size = 88, className = "" }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden data-testid="gfx-envelope">
      <rect x="15" y="30" width="70" height="50" rx="6" fill={V} />
      <path d="M 15 30 L 50 60 L 85 30" fill="none" stroke={B} strokeWidth="4" strokeLinejoin="round" />
      <circle cx="72" cy="24" r="8" fill={M} />
    </svg>
  );
}

// Little polaroid — for memories
export function PolaroidIcon({ size = 88, className = "" }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden data-testid="gfx-polaroid">
      <rect x="18" y="18" width="64" height="70" rx="4" fill="#FFFFFF" stroke={A} strokeOpacity="0.15" />
      <rect x="24" y="24" width="52" height="42" fill={S} />
      <circle cx="42" cy="40" r="6" fill={M} />
      <path d="M 24 66 L 40 50 L 52 60 L 76 40 L 76 66 Z" fill={V} opacity="0.85" />
      <rect x="24" y="72" width="34" height="4" fill={A} opacity="0.4" />
    </svg>
  );
}

// Little book — for journal
export function BookIcon({ size = 88, className = "" }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden data-testid="gfx-book">
      <rect x="20" y="18" width="60" height="70" rx="4" fill={M} />
      <rect x="24" y="22" width="52" height="62" rx="2" fill="#FFFFFF" />
      <line x1="32" y1="34" x2="68" y2="34" stroke={A} strokeOpacity="0.5" strokeWidth="1.5" />
      <line x1="32" y1="42" x2="60" y2="42" stroke={A} strokeOpacity="0.4" strokeWidth="1.5" />
      <line x1="32" y1="50" x2="66" y2="50" stroke={A} strokeOpacity="0.4" strokeWidth="1.5" />
      <line x1="32" y1="58" x2="54" y2="58" stroke={A} strokeOpacity="0.35" strokeWidth="1.5" />
      <circle cx="70" cy="72" r="6" fill={V} />
    </svg>
  );
}

// Milestone star burst
export function MilestoneIcon({ size = 88, className = "" }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden data-testid="gfx-milestone">
      <path d="M 50 12 L 58 42 L 88 50 L 58 58 L 50 88 L 42 58 L 12 50 L 42 42 Z" fill={V} />
      <circle cx="50" cy="50" r="10" fill={M} />
    </svg>
  );
}

/* ============ HERO / LARGE DECORATIONS ============ */

// Concentric arcs — motherhood journey stages
export function StageArcs({ width = 520, height = 260, className = "" }) {
  const arcs = [
    { r: 220, color: A, opacity: 0.9 },
    { r: 175, color: V, opacity: 0.9 },
    { r: 130, color: M, opacity: 0.95 },
    { r: 85,  color: S, opacity: 0.9 },
  ];
  return (
    <div className={className} aria-hidden data-testid="gfx-stage-arcs">
      <svg viewBox="0 0 520 260" width={width} height={height} className="max-w-full">
        {arcs.map((a, i) => (
          <motion.path
            key={i}
            d={`M ${260 - a.r} 260 A ${a.r} ${a.r} 0 0 1 ${260 + a.r} 260`}
            fill="none"
            stroke={a.color}
            strokeWidth="10"
            strokeLinecap="round"
            opacity={a.opacity}
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: i * 0.18, ease: "easeOut" }}
          />
        ))}
        <motion.circle cx="260" cy="260" r="12" fill={A}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ delay: 1.3 }}
        />
      </svg>
    </div>
  );
}

// Weekly pill counter — colorful dots grid
export function WeekDots({ current = 12, total = 40, className = "" }) {
  return (
    <div className={className} aria-hidden data-testid="gfx-week-dots">
      <svg viewBox="0 0 400 200" width="100%" className="max-w-full">
        {Array.from({ length: total }, (_, i) => {
          const cols = 10;
          const cx = 25 + (i % cols) * 38;
          const cy = 25 + Math.floor(i / cols) * 45;
          const filled = i < current;
          return (
            <motion.circle
              key={i} cx={cx} cy={cy} r="12"
              fill={filled ? (i < 13 ? V : i < 26 ? M : S) : "transparent"}
              stroke={filled ? "none" : A} strokeOpacity="0.15" strokeWidth="1"
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: i * 0.02, duration: 0.4 }}
            />
          );
        })}
      </svg>
    </div>
  );
}

/* ============ AMBIENT BACKGROUND SHAPES (kept, colored up) ============ */

// Large seed w/ orbit rings — colorful now
export function OrbitSeedGraphic({ size = 400, className = "", opacity = 1 }) {
  return (
    <div className={className} style={{ opacity }} aria-hidden data-testid="gfx-orbit-seed">
      <svg viewBox="0 0 400 400" width={size} height={size} className="max-w-full">
        {[180, 140, 100].map((r, i) => (
          <motion.circle key={r} cx="200" cy="200" r={r}
            fill="none" stroke={[V, M, S][i]} strokeWidth="2" opacity={0.6 - i * 0.12}
            initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
            transition={{ duration: 1.8, delay: i * 0.2 }}
          />
        ))}
        <motion.ellipse cx="200" cy="200" rx="44" ry="62" fill={V}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
        />
        <motion.circle cx="200" cy="200" r="20" fill={M}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.9 }}
        />
        <motion.circle cx="360" cy="120" r="8" fill={M}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.4 }} />
        <motion.circle cx="60" cy="180" r="6" fill={S}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.6 }} />
        <motion.circle cx="300" cy="80" r="5" fill={V}
          initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.8 }} />
      </svg>
    </div>
  );
}

// Curve weave — colorful
export function CurveWeaveGraphic({ width = 520, height = 240, className = "" }) {
  return (
    <div className={className} aria-hidden data-testid="gfx-curve-weave">
      <svg viewBox="0 0 520 240" width={width} height={height} className="max-w-full">
        {[{ y: 60, c: V }, { y: 100, c: M }, { y: 140, c: S }, { y: 180, c: A }].map((r, i) => (
          <motion.path key={i}
            d={`M -20 ${r.y} Q 260 ${r.y - 60} 540 ${r.y}`}
            fill="none" stroke={r.c} strokeWidth="4" strokeLinecap="round"
            opacity="0.85"
            initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
            transition={{ duration: 1.6, delay: i * 0.15, ease: "easeOut" }}
          />
        ))}
      </svg>
    </div>
  );
}

/* ============ MOOD ILLUSTRATIONS (for check-in) ============ */

export function MoodSun({ size = 56 }) {
  return (
    <svg viewBox="0 0 60 60" width={size} height={size} aria-hidden data-testid="mood-sun">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line key={a} x1="30" y1="8" x2="30" y2="14"
          stroke={M} strokeWidth="3" strokeLinecap="round"
          transform={`rotate(${a} 30 30)`}
        />
      ))}
      <circle cx="30" cy="30" r="12" fill={M} />
      <circle cx="30" cy="30" r="7" fill={V} />
    </svg>
  );
}

export function MoodSparkle({ size = 56 }) {
  return (
    <svg viewBox="0 0 60 60" width={size} height={size} aria-hidden data-testid="mood-sparkle">
      <path d="M 30 8 L 34 26 L 52 30 L 34 34 L 30 52 L 26 34 L 8 30 L 26 26 Z" fill={V} />
      <circle cx="30" cy="30" r="6" fill={M} />
    </svg>
  );
}

export function MoodMoon({ size = 56 }) {
  return (
    <svg viewBox="0 0 60 60" width={size} height={size} aria-hidden data-testid="mood-moon">
      <circle cx="30" cy="30" r="18" fill={S} />
      <circle cx="36" cy="26" r="14" fill={"#FBF4E8"} />
      <circle cx="18" cy="20" r="1.5" fill={M} />
      <circle cx="46" cy="42" r="1.5" fill={M} />
      <circle cx="14" cy="40" r="1" fill={M} />
    </svg>
  );
}

export function MoodCloud({ size = 56 }) {
  return (
    <svg viewBox="0 0 60 60" width={size} height={size} aria-hidden data-testid="mood-cloud">
      <ellipse cx="24" cy="28" rx="12" ry="10" fill={S} opacity="0.9" />
      <ellipse cx="36" cy="30" rx="14" ry="11" fill={S} />
      <line x1="20" y1="46" x2="18" y2="52" stroke={V} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="30" y1="46" x2="28" y2="54" stroke={V} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="40" y1="46" x2="38" y2="52" stroke={V} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
